import { useBandContext } from "Bands/contexts";
import { BAND_PATHS } from "Bands/router";
import { IBandInvitation, IBandUser } from "Bands/types";
import { has } from "lodash-es";
import { useMemo } from "react";
import { copy } from "shared/assets";
import { AddressInfo, FancyLink, Phone, TableColumn } from "shared/components";
import { isoToDateString, mapUserPermissions } from "shared/utils";
import { useBandRegionalSettings, useBandRoute } from ".";

const {
  members: { table: tableCopy },
} = copy;

export const useGetMemberTableColumns = (user: IBandUser | IBandInvitation) => {
  const userHasViewFullProfile = useMemo(
    () =>
      user &&
      "permissions" in user &&
      !!mapUserPermissions(user.permissions || []).VIEW_FULL_PROFILE,
    [user]
  );

  const { getCategory } = useBandContext();
  const { getPath } = useBandRoute();

  const showContactInfo = useMemo(() => has(user, "email"), [user]);
  const isMember = useMemo(() => has(user, "user"), [user]);
  const { dateFormat } = useBandRegionalSettings();

  const columns = useMemo(() => {
    let cols: TableColumn[] = [];

    cols.push({
      Header: tableCopy.name,
      accessor: "firstname",
      Cell: ({ row }) => {
        let user, to;

        if (isMember) {
          user = row.original as IBandUser;
          to = getPath(BAND_PATHS.VIEW_MEMBER, {
            params: { userId: user.id },
          });
        } else {
          user = row.original as IBandInvitation;
        }

        const label = `${user.firstname} ${user.lastname}`;

        if (!userHasViewFullProfile || !to) return label;

        return <FancyLink to={to}>{label}</FancyLink>;
      },
    });

    cols.push(
      {
        Header: tableCopy.nickName,
        accessor: "nickname",
      },
      {
        Header: tableCopy.category,
        Cell: ({ row }) => {
          const { category } = isMember
            ? (row.original as IBandUser)
            : (row.original as IBandInvitation);

          return getCategory(category?.id)?.label || "-";
        },
      },
      {
        Header: tableCopy.function,
        accessor: "function",
      }
    );

    if (showContactInfo) {
      cols.push({
        Header: tableCopy.email,
        accessor: "email",
      });
    }

    // only members have address, phone number, dob properties
    if (showContactInfo && isMember) {
      cols.push(
        {
          Header: tableCopy.address,
          accessor: "addresses",
          Cell: ({ value }) =>
            value && value[0] ? (
              <AddressInfo
                address={value[0].address}
                showFlag={false}
                format="country_last"
              />
            ) : null,
        },
        {
          Header: tableCopy.phoneNumber,
          accessor: "phoneNumbers",
          Cell: ({ value }) =>
            value && value[0] ? <Phone phone={value[0].phoneNumber} /> : null,
        },
        {
          Header: tableCopy.birthDate,
          accessor: "birthDate",
          Cell: ({ value }) =>
            value ? isoToDateString(value, dateFormat) : null,
        }
      );
    }

    return cols;
  }, [
    showContactInfo,
    isMember,
    userHasViewFullProfile,
    getPath,
    getCategory,
    dateFormat,
  ]);

  return { columns };
};
